import { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { Node } from 'reactflow';
import ConfirmationModals from '@components/EntityDrawers/components/ConfirmationModals';
import ProjectRightDrawer, {
  ProjectRightDrawerProps,
} from '@components/EntityDrawers/components/ProjectRightDrawer';
import useDrawerGate from '@components/EntityDrawers/hooks/useDrawerGate';
import useNodeDrawer, {
  EntityDrawerOptions,
} from '@components/EntityDrawers/hooks/useNodeDrawer';
import { DrawerProvider } from '@components/EntityDrawers/providers/DrawerProvider';
import { SubLayerTypes } from '@constants/canvas/layers';
import { ProjectDrawerTypes } from '@constants/entities/drawers';

export type DrawerWrapperProps<T, F = {}> = {
  type: T;
  drawerProps?: Pick<ProjectRightDrawerProps, 'tab' | 'headChildren'>;
  canSave?: (
    values: F,
    editMode: boolean,
    activeNode: Node | undefined,
  ) => PromiseLike<boolean>;
};

/**
 * Turns a drawer's kebab-case wire value into a component name, so drawers are
 * identifiable in React DevTools and in profiler output:
 * `edit-assessment-scope-entity` becomes `AssessmentScope`.
 */
const toComponentName = (drawerType: ProjectDrawerTypes) =>
  drawerType
    .replace(/^(add|edit)-/, '')
    .replace(/-entity$/, '')
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

export type DrawerContentProps = { tab?: number };
export type DrawerSettings<FormValuesType, MetaType> = Omit<
  EntityDrawerOptions<FormValuesType, MetaType>,
  'type'
>;

const withDrawer = <FormValuesType, MetaType, Type extends ProjectDrawerTypes>(
  DrawerContent: FC<DrawerContentProps>,
  options: DrawerSettings<FormValuesType, MetaType>,
) => {
  const DrawerBody = ({
    type,
    drawerProps,
    canSave,
  }: DrawerWrapperProps<Type, FormValuesType>) => {
    const {
      form,
      handleSave,
      isSaveDisabled,
      isIncompleteMessageShown,
      isReviewRequired,
      handleClose,
      drawerTitle,
      badge,
      tag,
      editMode,
      modalsProps,
      activeNode,
    } = useNodeDrawer<FormValuesType, MetaType>({ type, ...options });

    const handleSaveClick = async (saveOpts?: { fromShortcut?: boolean }) => {
      const canProceed = await canSave?.(
        form.getValues(),
        editMode,
        activeNode,
      );
      if (canProceed || !canSave) {
        await handleSave(saveOpts);
      }
    };

    return (
      <>
        <DrawerProvider
          value={{
            subLayer: (activeNode?.parentNode ?? '') as SubLayerTypes,
            editMode,
            isIncomplete: isIncompleteMessageShown,
            isReviewRequired,
          }}
        >
          <ConfirmationModals {...modalsProps} />

          <FormProvider {...form}>
            <ProjectRightDrawer
              title={drawerTitle}
              onClose={handleClose}
              disabledSave={isSaveDisabled}
              onSave={handleSaveClick}
              badge={badge}
              tag={tag}
              {...drawerProps}
            >
              <DrawerContent tab={drawerProps?.tab} />
            </ProjectRightDrawer>
          </FormProvider>
        </DrawerProvider>
      </>
    );
  };

  const DrawerGate = (props: DrawerWrapperProps<Type, FormValuesType>) => {
    const { type } = props;
    const open = useDrawerGate(type, options.editType);

    if (!open) return null;

    return <DrawerBody {...props} />;
  };

  const name = toComponentName(options.editType);

  DrawerBody.displayName = `${name}DrawerBody`;
  DrawerGate.displayName = `${name}Drawer`;

  return DrawerGate;
};

export default withDrawer;
